import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getBudget, createBudgetItem, updateBudgetItem, deleteBudgetItem, autoEstimateBudget, applyAutoEstimate } from "@/services/productionService";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DollarSign, Plus, Trash2, Loader2, Sparkles, TrendingUp,
  TrendingDown, ChevronDown, ChevronRight, FileText, AlertCircle, CheckCircle2
} from "lucide-react";


const CATEGORY_LABELS = {
  above_the_line: "Above the Line",
  below_the_line_production: "Below the Line - Production",
  below_the_line_post: "Below the Line - Post Production",
  other: "Other",
};

const CATEGORY_COLORS = {
  above_the_line: "text-amber-400",
  below_the_line_production: "text-blue-400",
  below_the_line_post: "text-purple-400",
  other: "text-emerald-400",
};

function formatINR(amount) {
  if (amount >= 10000000) return `${(amount / 10000000).toFixed(2)} Cr`;
  if (amount >= 100000) return `${(amount / 100000).toFixed(2)} L`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
  return amount.toLocaleString("en-IN");
}

function BudgetSummaryCard({ label, value, icon: Icon, color, subtext }) {
  return (
    <div className="p-4 rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-elevated)]">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center`}>
          <Icon size={16} />
        </div>
        <span className="text-xs text-[var(--text-muted)]">{label}</span>
      </div>
      <p className="text-xl font-semibold text-[var(--text-primary)] tabular-nums">{formatINR(value)}</p>
      {subtext && <p className="text-[10px] text-[var(--text-disabled)] mt-1">{subtext}</p>}
    </div>
  );
}

function BudgetRow({ item, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(item);

  const save = () => {
    onUpdate(item.id, editData);
    setEditing(false);
  };

  if (editing) {
    return (
      <tr className="border-b border-[var(--border-subtle)]">
        <td className="py-2 px-3"><Input value={editData.description} onChange={(e) => setEditData({ ...editData, description: e.target.value })} className="input text-xs h-7 w-full" /></td>
        <td className="py-2 px-3"><Input type="number" value={editData.quantity} onChange={(e) => setEditData({ ...editData, quantity: Number(e.target.value) })} className="input text-xs h-7 w-20" /></td>
        <td className="py-2 px-3"><Input type="number" value={editData.rate} onChange={(e) => setEditData({ ...editData, rate: Number(e.target.value) })} className="input text-xs h-7 w-24" /></td>
        <td className="py-2 px-3 text-right text-xs text-[var(--text-primary)] tabular-nums">{formatINR(editData.rate * editData.quantity)}</td>
        <td className="py-2 px-3"><Input type="number" value={editData.actual} onChange={(e) => setEditData({ ...editData, actual: Number(e.target.value) })} className="input text-xs h-7 w-24" /></td>
        <td className="py-2 px-3">
          <div className="flex gap-1">
            <button onClick={save} className="text-emerald-400 hover:text-emerald-300"><CheckCircle2 size={14} /></button>
            <button onClick={() => setEditing(false)} className="text-[var(--text-disabled)] hover:text-[var(--text-muted)]"><AlertCircle size={14} /></button>
          </div>
        </td>
      </tr>
    );
  }

  const variance = item.estimated - item.actual;
  return (
    <tr className="border-b border-[var(--border-subtle)] hover:bg-[var(--bg-hover)] transition-colors group" data-testid={`budget-row-${item.id}`}>
      <td className="py-2 px-3 text-xs text-[var(--text-primary)]">{item.description}</td>
      <td className="py-2 px-3 text-xs text-[var(--text-muted)] tabular-nums">{item.quantity} {item.unit !== "fixed" ? `(${item.unit})` : ""}</td>
      <td className="py-2 px-3 text-xs text-[var(--text-muted)] tabular-nums">{formatINR(item.rate)}</td>
      <td className="py-2 px-3 text-xs text-[var(--text-primary)] tabular-nums text-right font-medium">{formatINR(item.estimated)}</td>
      <td className="py-2 px-3 text-xs tabular-nums text-right">{item.actual > 0 ? formatINR(item.actual) : <span className="text-[var(--text-disabled)]">-</span>}</td>
      <td className="py-2 px-3">
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => { setEditData(item); setEditing(true); }} className="text-[var(--text-disabled)] hover:text-[var(--color-gold-400)]"><FileText size={13} /></button>
          <button onClick={() => onDelete(item.id)} className="text-[var(--text-disabled)] hover:text-[var(--color-error)]"><Trash2 size={13} /></button>
        </div>
      </td>
    </tr>
  );
}

export default function BudgetPage({ projectId, project, script, characters, locations }) {
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [autoEstimate, setAutoEstimate] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState("standard");
  const [expandedCats, setExpandedCats] = useState({});
  const [newItem, setNewItem] = useState({ category: "above_the_line", subcategory: "", description: "", quantity: 1, rate: 0, unit: "fixed" });

  const fetchBudget = useCallback(async () => {
    try {
      const data = await getBudget(projectId);
      setBudget(data);
      const expanded = {};
      Object.keys(data.by_category || {}).forEach(k => { expanded[k] = true; });
      setExpandedCats(expanded);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchBudget(); }, [fetchBudget]);

  const handleAddItem = async () => {
    try {
      await createBudgetItem({ ...newItem, project_id: projectId });
      toast.success("Item added");
      setShowAddDialog(false);
      setNewItem({ category: "above_the_line", subcategory: "", description: "", quantity: 1, rate: 0, unit: "fixed" });
      fetchBudget();
    } catch { toast.error("Failed to add item"); }
  };

  const handleUpdateItem = async (itemId, data) => {
    try {
      await updateBudgetItem(itemId, data);
      fetchBudget();
    } catch { toast.error("Failed to update"); }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await deleteBudgetItem(itemId);
      fetchBudget();
    } catch { toast.error("Failed to delete"); }
  };

  const handleAutoEstimate = async () => {
    setAutoLoading(true);
    try {
      const data = await autoEstimateBudget(projectId, { tier: selectedTier });
      setAutoEstimate(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auto-estimate failed");
    } finally { setAutoLoading(false); }
  };

  const handleApplyAutoEstimate = async () => {
    if (!autoEstimate) return;
    try {
      await applyAutoEstimate(projectId, { items: autoEstimate.items });
      toast.success("Budget applied");
      setAutoEstimate(null);
      fetchBudget();
    } catch { toast.error("Failed to apply"); }
  };

  const toggleCategory = (key) => setExpandedCats({ ...expandedCats, [key]: !expandedCats[key] });

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="animate-spin text-[var(--color-gold-400)]" size={32} />
      </div>
    );
  }

  const totalEstimated = budget?.total_estimated || 0;
  const totalActual = budget?.total_actual || 0;
  const variance = totalEstimated - totalActual;
  const items = budget?.items || [];

  return (
    <div className="h-full overflow-y-auto bg-[var(--bg-app)]" data-testid="budget-page">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">Budget Estimator Pro</h2>
            <p className="text-sm text-[var(--text-muted)] mt-1">{items.length} line items</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={selectedTier} onValueChange={setSelectedTier}>
              <SelectTrigger className="w-28 h-8 text-xs bg-[var(--bg-elevated)] border-[var(--border-subtle)]" data-testid="tier-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--bg-surface)] border-[var(--border-default)]">
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="standard">Standard</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleAutoEstimate} disabled={autoLoading} className="btn-secondary text-xs h-8" size="sm" data-testid="auto-estimate-btn">
              {autoLoading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
              Auto Estimate
            </Button>
            <Button onClick={() => setShowAddDialog(true)} className="btn-primary text-xs h-8" size="sm" data-testid="add-budget-item-btn">
              <Plus size={14} className="mr-1" /> Add Item
            </Button>
          </div>
        </div>

        {/* Auto Estimate Preview */}
        {autoEstimate && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 rounded-xl border border-[var(--color-gold-500)]/30 bg-[var(--color-gold-500)]/5">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-semibold text-[var(--color-gold-400)]">Auto-Generated Estimate ({autoEstimate.tier} tier)</h3>
                <p className="text-xs text-[var(--text-muted)]">
                  {autoEstimate.num_scenes} scenes | {autoEstimate.num_characters} characters | {autoEstimate.num_locations} locations | {autoEstimate.shoot_days} days
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold text-[var(--text-primary)]">{formatINR(autoEstimate.total_estimated)}</span>
                <Button onClick={() => setAutoEstimate(null)} className="btn-secondary text-xs h-7" size="sm">Dismiss</Button>
                <Button onClick={handleApplyAutoEstimate} className="btn-primary text-xs h-7" size="sm" data-testid="apply-estimate-btn">
                  <CheckCircle2 size={13} className="mr-1" /> Apply
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <BudgetSummaryCard label="Total Estimated" value={totalEstimated} icon={DollarSign} color="bg-[var(--color-gold-500)]/10 text-[var(--color-gold-400)]" subtext={`${items.length} line items`} />
          <BudgetSummaryCard label="Total Actual" value={totalActual} icon={totalActual > 0 ? TrendingUp : DollarSign} color="bg-blue-500/10 text-blue-400" subtext={totalActual > 0 ? "Expenses tracked" : "No actuals yet"} />
          <BudgetSummaryCard label="Variance" value={Math.abs(variance)} icon={variance >= 0 ? TrendingDown : TrendingUp} color={variance >= 0 ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"} subtext={variance >= 0 ? "Under budget" : "Over budget"} />
        </div>

        {/* Budget Table by Category */}
        {items.length === 0 ? (
          <div className="text-center py-16 text-[var(--text-muted)]">
            <DollarSign size={40} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">No budget items yet</p>
            <p className="text-xs mt-1">Add manually or use Auto Estimate to generate from your project data</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.keys(CATEGORY_LABELS).map(catKey => {
              const catItems = items.filter(it => it.category === catKey);
              if (catItems.length === 0) return null;
              const catTotal = catItems.reduce((s, it) => s + (it.estimated || 0), 0);
              const expanded = expandedCats[catKey];

              return (
                <div key={catKey} className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-elevated)] overflow-hidden">
                  <button
                    onClick={() => toggleCategory(catKey)}
                    className="w-full flex items-center justify-between p-4 hover:bg-[var(--bg-hover)] transition-colors"
                    data-testid={`budget-cat-${catKey}`}
                  >
                    <div className="flex items-center gap-3">
                      {expanded ? <ChevronDown size={16} className="text-[var(--text-muted)]" /> : <ChevronRight size={16} className="text-[var(--text-muted)]" />}
                      <span className={`text-sm font-semibold ${CATEGORY_COLORS[catKey]}`}>{CATEGORY_LABELS[catKey]}</span>
                      <span className="text-xs text-[var(--text-disabled)]">{catItems.length} items</span>
                    </div>
                    <span className="text-sm font-semibold text-[var(--text-primary)] tabular-nums">{formatINR(catTotal)}</span>
                  </button>

                  <AnimatePresence>
                    {expanded && (
                      <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
                        <table className="w-full text-left">
                          <thead>
                            <tr className="border-t border-b border-[var(--border-subtle)] bg-[var(--bg-muted)]">
                              <th className="py-2 px-3 text-[10px] text-[var(--text-disabled)] font-medium uppercase tracking-wider">Description</th>
                              <th className="py-2 px-3 text-[10px] text-[var(--text-disabled)] font-medium uppercase tracking-wider">Qty</th>
                              <th className="py-2 px-3 text-[10px] text-[var(--text-disabled)] font-medium uppercase tracking-wider">Rate</th>
                              <th className="py-2 px-3 text-[10px] text-[var(--text-disabled)] font-medium uppercase tracking-wider text-right">Estimated</th>
                              <th className="py-2 px-3 text-[10px] text-[var(--text-disabled)] font-medium uppercase tracking-wider text-right">Actual</th>
                              <th className="py-2 px-3 w-16"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {catItems.map(item => (
                              <BudgetRow key={item.id} item={item} onUpdate={handleUpdateItem} onDelete={handleDeleteItem} />
                            ))}
                          </tbody>
                        </table>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Item Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[var(--bg-surface)] border-[var(--border-default)] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg text-[var(--text-primary)]">Add Budget Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div>
              <label className="text-xs text-[var(--text-muted)] mb-1 block">Category</label>
              <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                <SelectTrigger className="input text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-surface)] border-[var(--border-default)]">
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[var(--text-muted)] mb-1 block">Description</label>
              <Input value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} className="input text-sm" placeholder="e.g. Camera Rental" data-testid="new-item-description" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">Quantity</label>
                <Input type="number" value={newItem.quantity} onChange={(e) => setNewItem({ ...newItem, quantity: Number(e.target.value) })} className="input text-sm" />
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">Rate (INR)</label>
                <Input type="number" value={newItem.rate} onChange={(e) => setNewItem({ ...newItem, rate: Number(e.target.value) })} className="input text-sm" data-testid="new-item-rate" />
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">Unit</label>
                <Select value={newItem.unit} onValueChange={(v) => setNewItem({ ...newItem, unit: v })}>
                  <SelectTrigger className="input text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-surface)] border-[var(--border-default)]">
                    <SelectItem value="fixed">Fixed</SelectItem>
                    <SelectItem value="per day">Per Day</SelectItem>
                    <SelectItem value="per week">Per Week</SelectItem>
                    <SelectItem value="per location">Per Location</SelectItem>
                    <SelectItem value="per character">Per Character</SelectItem>
                    <SelectItem value="project">Project</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleAddItem} className="btn-primary w-full text-sm" data-testid="create-item-submit">
              <Plus size={14} className="mr-1" /> Add Item
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
